<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type {
  GaussianSplatHandle,
  ScalarFilterOptions,
  ScalarHistogramResult,
} from '@sharefe/share3d-engine'
import {
  buildScalarHistogramBars,
  formatScalarRange,
  normalizeScalarRange,
  resolveScalarHistogramRange,
} from '../utils/gsplatScalarFilter'

type ScalarType = ScalarFilterOptions['type']

const props = withDefaults(
  defineProps<{
    /** 当前场景内最新的 3DGS 句柄；为空时卡片只展示提示。 */
    handle?: GaussianSplatHandle | null
    /** 外部禁用（例如模型加载中）。 */
    disabled?: boolean
  }>(),
  { handle: null, disabled: false },
)

const TYPE_OPTIONS: ReadonlyArray<{ value: ScalarType; label: string; hint: string }> = [
  { value: 'volume', label: '体积', hint: 'sx · sy · sz' },
  { value: 'surface-area', label: '表面积', hint: 'sx² + sy² + sz²' },
  { value: 'value', label: '亮度', hint: 'BT.601 luma' },
]

const BIN_COUNT = 64

const scalarType = ref<ScalarType>('volume')
const filterEnabled = ref(false)
const minValue = ref(0)
const maxValue = ref(1)
const histogram = ref<ScalarHistogramResult | null>(null)
const loading = ref(false)
const message = ref('')

const hasHandle = computed(() => props.handle !== null && props.handle !== undefined)
const activeOption = computed(
  () => TYPE_OPTIONS.find((option) => option.value === scalarType.value) ?? TYPE_OPTIONS[0],
)
const bars = computed(() => buildScalarHistogramBars(histogram.value?.bins ?? []))
const summary = computed(() => {
  const result = histogram.value
  if (!result) return '暂无数据'
  return `样本 ${formatNumber(result.sampleCount)} · 范围 ${formatScalarRange(result.min, result.max)}`
})
const rangeHint = computed(
  () => `当前过滤 ${formatScalarRange(minValue.value, maxValue.value)}`,
)
const cardBadge = computed(() => {
  if (filterEnabled.value) return activeOption.value.label
  return histogram.value ? '未过滤' : '—'
})

watch(
  () => props.handle,
  () => {
    filterEnabled.value = false
    void refreshHistogram()
  },
  { immediate: true },
)

watch(scalarType, () => {
  void refreshHistogram()
})

async function refreshHistogram(): Promise<void> {
  const handle = props.handle
  histogram.value = null
  message.value = ''
  if (!handle) {
    message.value = '请先加载 3DGS 模型，再使用标量过滤。'
    return
  }
  loading.value = true
  try {
    // 句柄在模型挂载后即可拿到，但 World 状态要等首帧建立，早期查询会返回 null；
    // 这里做有限重试，避免用户看到误导性的“无数据”。
    const result = await fetchHistogramWithRetry(handle)
    if (props.handle !== handle) return
    histogram.value = result
    const range = resolveScalarHistogramRange(result)
    if (range) {
      minValue.value = round(range.min)
      maxValue.value = round(range.max)
      message.value = ''
      if (filterEnabled.value) applyFilter()
    } else {
      message.value = '当前资产未提供标量直方图数据（可能尚未就绪或缺少 CPU 源数据）。'
    }
  } catch (error) {
    message.value = error instanceof Error ? error.message : String(error)
  } finally {
    loading.value = false
  }
}

async function fetchHistogramWithRetry(
  handle: GaussianSplatHandle,
): Promise<ScalarHistogramResult | null> {
  for (let attempt = 0; attempt < 6; attempt += 1) {
    if (props.handle !== handle) return null
    const result = await handle.getScalarHistogram({
      type: scalarType.value,
      binCount: BIN_COUNT,
      scale: 'linear',
      percentileRange: { lower: 1, upper: 99 },
    })
    if (result) return result
    await new Promise((resolve) => window.setTimeout(resolve, 400))
  }
  return null
}

function applyFilter(): void {
  const handle = props.handle
  if (!handle) return
  message.value = ''
  if (!filterEnabled.value) {
    handle.clearScalarFilter()
    return
  }
  const applied = handle.setScalarFilter({
    enabled: true,
    type: scalarType.value,
    min: minValue.value,
    max: maxValue.value,
  })
  if (!applied) {
    filterEnabled.value = false
    message.value = '标量过滤参数无效，或当前后端尚未激活该能力。'
  }
}

function toggleFilter(): void {
  filterEnabled.value = !filterEnabled.value
  applyFilter()
}

function resetRange(): void {
  const range = resolveScalarHistogramRange(histogram.value)
  if (!range) return
  minValue.value = round(range.min)
  maxValue.value = round(range.max)
  if (filterEnabled.value) applyFilter()
}

function onRangeCommit(): void {
  const normalized = normalizeScalarRange(minValue.value, maxValue.value)
  if (normalized) {
    minValue.value = normalized.min
    maxValue.value = normalized.max
  }
  if (filterEnabled.value) applyFilter()
}

function round(value: number): number {
  if (!Number.isFinite(value)) return 0
  if (value === 0) return 0
  // 用有效数字而不是定点小数，避免量级很小的下界被压成 0。
  const magnitude = Math.abs(value)
  const digits = magnitude >= 100 ? 4 : 6
  return Number(value.toPrecision(digits))
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('zh-CN')
}

</script>

<template>
  <details class="panel-section" open>
    <summary class="panel-section-summary">
      <span class="summary-main">
        <span class="panel-section-title">标量过滤</span>
        <span class="panel-section-subtitle">按体积 / 表面积 / 亮度筛选高斯点，并查看直方图</span>
      </span>
      <span class="summary-side">
        <span class="summary-count">{{ cardBadge }}</span>
        <span class="summary-chevron" aria-hidden="true">›</span>
      </span>
    </summary>

    <div class="panel-section-body">
      <div class="button-grid button-grid-three">
        <button
          v-for="option in TYPE_OPTIONS"
          :key="option.value"
          class="panel-button"
          :class="{ active: scalarType === option.value }"
          type="button"
          :disabled="disabled || loading"
          :title="option.hint"
          @click="scalarType = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="scalar-histogram" :class="{ 'is-empty': bars.length === 0 }" aria-hidden="true">
        <span
          v-for="bar in bars"
          :key="bar.index"
          class="scalar-histogram-bar"
          :style="{ height: `${bar.percent}%` }"
        />
      </div>

      <div class="status-grid">
        <div class="status-item">
          <span class="status-label">分布</span>
          <strong class="status-value">{{ summary }}</strong>
        </div>
        <div class="status-item">
          <span class="status-label">过滤区间</span>
          <strong class="status-value">{{ rangeHint }}</strong>
        </div>
      </div>

      <label class="panel-field">
        <span class="panel-field-label">下界</span>
        <input
          v-model.number="minValue"
          class="panel-input"
          type="number"
          step="any"
          :disabled="disabled || !hasHandle"
          @change="onRangeCommit"
        />
      </label>
      <input
        v-model.number="minValue"
        class="scalar-slider"
        type="range"
        :min="histogram?.min ?? 0"
        :max="histogram?.max ?? 1"
        step="any"
        :disabled="disabled || !hasHandle"
        @change="onRangeCommit"
      />

      <label class="panel-field">
        <span class="panel-field-label">上界</span>
        <input
          v-model.number="maxValue"
          class="panel-input"
          type="number"
          step="any"
          :disabled="disabled || !hasHandle"
          @change="onRangeCommit"
        />
      </label>
      <input
        v-model.number="maxValue"
        class="scalar-slider"
        type="range"
        :min="histogram?.min ?? 0"
        :max="histogram?.max ?? 1"
        step="any"
        :disabled="disabled || !hasHandle"
        @change="onRangeCommit"
      />

      <div class="button-grid">
        <button
          class="panel-button"
          :class="{ active: filterEnabled }"
          type="button"
          :disabled="disabled || !hasHandle || loading"
          @click="toggleFilter"
        >
          {{ filterEnabled ? '关闭标量过滤' : '启用标量过滤' }}
        </button>
        <button
          class="panel-button panel-button-subtle"
          type="button"
          :disabled="disabled || !hasHandle || loading || !histogram"
          @click="resetRange"
        >
          重置区间
        </button>
      </div>

      <button
        class="panel-button panel-button-wide panel-button-neutral"
        type="button"
        :disabled="disabled || !hasHandle || loading"
        @click="refreshHistogram"
      >
        {{ loading ? '统计中…' : '重新统计直方图' }}
      </button>

      <p class="panel-hint">{{ message || `标量定义：${activeOption.hint}；过滤后区间外的高斯点被隐藏。` }}</p>
    </div>
  </details>
</template>

<style scoped>
.scalar-histogram {
  display: flex;
  align-items: flex-end;
  gap: 1px;
  height: 72px;
  padding: 4px;
  border: 1px solid rgba(120, 190, 230, 0.25);
  border-radius: 6px;
  background: rgba(8, 18, 28, 0.6);
}

.scalar-histogram.is-empty::after {
  content: '无直方图数据';
  margin: auto;
  color: #6f8b9d;
  font-size: 12px;
}

.scalar-histogram-bar {
  flex: 1 1 0;
  min-width: 1px;
  border-radius: 1px 1px 0 0;
  background: linear-gradient(180deg, #6fd0ff, #2a7fb8);
}

.scalar-slider {
  width: 100%;
}
</style>
